// useDinamicTable.ts
import type { DinamicTableProps } from "./types";
import { Tooltip, Chip, Box, IconButton } from "@mui/material";
import AccionesTable from "./AccionesTable";
import { useMaterialUIController } from "context";
import _ from "lodash";
import { columnsNames } from "./columnsNames";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import PreviewIcon from "@mui/icons-material/Preview";

export const useDinamicTable = (props: DinamicTableProps) => {
  const [data, setData] = useState<any[]>(props?.data || []);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const estatusRequisicion = [
    {
      id: 1,
      tipo: "Creada",
      variant: "filled" as const,
      colorChip: "#3B82C4" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 2,
      tipo: "Autorizada Jefe Inmediato",
      variant: "filled" as const,
      colorChip: "#5B8DB8" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 3,
      tipo: "Autorizada Director de Area",
      variant: "filled" as const,
      colorChip: "#7B9E89" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 4,
      tipo: "Autorizada DAF",
      variant: "filled" as const,
      colorChip: "#C08A3E" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 5,
      tipo: "Autorizada DG",
      variant: "filled" as const,
      colorChip: "#D9A441" as const,
      colorTexto: "#000" as const,
    },
    {
      id: 6,
      tipo: "Atendida",
      variant: "filled" as const,
      colorChip: "#2E7D5B" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 7,
      tipo: "Rechazada",
      variant: "filled" as const,
      colorChip: "#B33A3A" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 8,
      tipo: "Pendiente DAF",
      variant: "filled" as const,
      colorChip: "#C08A3E" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 9,
      tipo: "Cancelada",
      variant: "filled" as const,
      colorChip: "#f9ff00" as const,
      colorTexto: "#000" as const,
    },
    {
      id: 10,
      tipo: "Pendiente DG",
      variant: "filled" as const,
      colorChip: "#C08A3E" as const,
      colorTexto: "#fff" as const,
    },
  ];

  const estatusOrdenCompra = [
    {
      id: 1,
      tipo: "Generada",
      variant: "filled" as const,
      colorChip: "#276291" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 2,
      tipo: "Enviada",
      variant: "filled" as const,
      colorChip: "#db7916" as const,
      colorTexto: "#000" as const,
    },
    {
      id: 4,
      tipo: "Aceptada",
      variant: "filled" as const,
      colorChip: "#53b828" as const,
      colorTexto: "#000" as const,
    },
    {
      id: 5,
      tipo: "Surtida",
      variant: "filled" as const,
      colorChip: "#eddf45" as const,
      colorTexto: "#000" as const,
    },
    {
      id: 6,
      tipo: "Rechazada",
      variant: "filled" as const,
      colorChip: "#ab1b3a" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 7,
      tipo: "XML Validado",
      variant: "filled" as const,
      colorChip: "#083812" as const,
      colorTexto: "#fff" as const,
    },
  ];

  const estatusCotizacion = [
    {
      id: 1,
      tipo: "Cancelada",
      variant: "filled" as const,
      colorChip: "#9c1327" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 2,
      tipo: "Activa",
      variant: "filled" as const,
      colorChip: "#2469b3" as const,
      colorTexto: "#fff" as const,
    },
    {
      id: 3,
      tipo: "Concluida",
      variant: "filled" as const,
      colorChip: "#4CAF50" as const,
      colorTexto: "#000" as const,
    },
  ];

  const esNumero = (valor: any) => {
    const valorLimpio = (valor + "").replace(/[%$,\s]/g, "");
    return !isNaN(Number(valorLimpio));
  };

  // Renderiza el valor de cada celda
  const renderCell = (value: any, row: any, key: string) => {
    if (key === "estatus" || key === "activo") {
      return (
        <Chip
          label={value === 1 || value === true ? "Activo" : "Inactivo"}
          color={value === 1 || value === true ? "success" : "error"}
          size="small"
        />
      );
    }
    if (key === "disponibilidad") {
      return (
        <Chip
          label={value === 1 || value === true ? "Disponible" : "No disponible"}
          color={value === 1 || value === true ? "success" : "error"}
          size="small"
        />
      );
    }
    if (key === "computo") {
      return (
        <Chip
          label={value === "si" ? "Equipo de cómputo" : "No es cómputo"}
          color={value === "si" ? "info" : "default"}
          size="small"
        />
      );
    }
    if (key === "subido") {
      return (
        <Chip
          label={value === 1 ? "Registrado" : "Sin registrar"}
          color={value === 1 ? "success" : "error"}
          size="small"
        />
      );
    }
    if (key === "detalles") {
      const totalProductos = value.length;
      return (
        <Chip
          label={`${totalProductos} producto${value !== 1 ? "s" : ""}`}
          color="primary"
          size="small"
          variant="outlined"
        />
      );
    }
    if (key === "estatus_requisicion") {
      return estatusRequisicionChip(value);
    }
    if (key === "estatus_orden") {
      return estatusOrdenCompraChip(value);
    }
    if (key === "estatus_cotizacion") {
      return estatusCotizacionChip(value);
    }
    if (key === "total_proveedores") {
      return (
        <Chip
          label={`${value} proveedor${value !== 1 ? "es" : ""}`}
          color="default"
          size="small"
          variant="outlined"
        />
      );
    }

    if (key === "proveedores_respondidos") {
      return (
        <Chip label={`${value} respondido${value !== 1 ? "s" : ""}`} color="success" size="small" />
      );
    }

    if (key === "id_requisicion" && props?.esListaCotizaciones) {
      return datosRequisicionCotizacion(value, row);
    }

    if (key === "proveedores_pendientes") {
      return (
        <Chip
          label={`${value} pendiente${value !== 1 ? "s" : ""}`}
          color={value > 0 ? "warning" : "success"}
          size="small"
        />
      );
    }

    const isDate = ["linea_base", "fecha_inicio", "fecha_fin", "fecha_final"].includes(key);
    const isDateTime = ["fecha_registro", "created_at", "updated_at"].includes(key);

    let text =
      value === 0
        ? "0"
        : isDate
          ? moment(value || "").format("DD/MM/YYYY")
          : isDateTime
            ? moment(value || "").format("DD/MM/YYYY HH:mm")
            : value ?? "";

    return (
      <Tooltip title={String(text)}>
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {String(text)}
        </span>
      </Tooltip>
    );
  };

  const estatusRequisicionChip = (id_estatus_requisicion: number) => {
    const estatusRequisicionText = estatusRequisicion.find(
      (e: any) => e?.id === id_estatus_requisicion
    );
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Chip
          label={estatusRequisicionText?.tipo || ""}
          variant={estatusRequisicionText?.variant}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "max-content",
            backgroundColor: estatusRequisicionText?.colorChip || "#e0e0e0",
            color: estatusRequisicionText?.colorTexto,
          }}
        />
      </Box>
    );
  };

  const estatusOrdenCompraChip = (id_estatus_orden_compra: number) => {
    const estatusOrdenCompraText = estatusOrdenCompra.find(
      (e: any) => e?.id === id_estatus_orden_compra
    );
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Chip
          label={estatusOrdenCompraText?.tipo || ""}
          variant={estatusOrdenCompraText?.variant}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "max-content",
            backgroundColor: estatusOrdenCompraText?.colorChip || "#e0e0e0",
            color: estatusOrdenCompraText?.colorTexto,
          }}
        />
      </Box>
    );
  };

  const estatusCotizacionChip = (id_estatus_orden_compra: number) => {
    const estatusCotizacionText = estatusCotizacion.find(
      (e: any) => e?.id === id_estatus_orden_compra
    );
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Chip
          label={estatusCotizacionText?.tipo || ""}
          variant={estatusCotizacionText?.variant}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "max-content",
            backgroundColor: estatusCotizacionText?.colorChip || "#e0e0e0",
            color: estatusCotizacionText?.colorTexto,
          }}
        />
      </Box>
    );
  };

  const datosRequisicionCotizacion = (value: any, row: any) => {
    return (
      <Tooltip title="Ver requisición">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => props?.enAccion && props?.enAccion("ver_requisicion", row)}
            sx={{ color: darkMode ? "#fff" : "#1d80c2" }}
          >
            <PreviewIcon fontSize="large" />
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  const buildMDColumns = () => {
    if (!props?.data?.length) return { columns: [], rows: [] };

    const rawKeys = Object.keys(props.data[0]);
    const keys = props?.columnsOrder
      ? props.columnsOrder.filter((k) => rawKeys.includes(k))
      : props?.columnsToShow
        ? rawKeys.filter((k) => props.columnsToShow.includes(k))
        : rawKeys;

    const actionColumn = props?.actions
      ? [
        {
          Header: "Herramientas",
          accessor: "__acciones__",
          align: "center" as const,
          width: "50px",
          disableSortBy: true,
        },
      ]
      : [];

    const dataColumns = keys.map((key) => ({
      Header:
        columnsNames.find((c: any) => c?.id === key)?.value ||
        (key[0].toUpperCase() + key.substring(1)).replaceAll("_", " "),
      accessor: key,
      align: esNumero(props.data[0]?.[key]) ? ("center" as const) : ("left" as const),
    }));

    const columns = [...actionColumn, ...dataColumns];

    const rows = data.map((item) => {
      const row: Record<string, any> = {};

      if (props?.actions) {
        row["__acciones__"] = (
          <AccionesTable
            esListaEmpresas={props?.esListaEmpresas}
            esListaDocumentos={props?.esListaDocumentos}
            esListaInventario={props?.esListaInventario}
            esListaRequisicion={props?.esListaRequisicion}
            esListaProveedores={props?.esListaProveedores}
            esListaCotizaciones={props?.esListaCotizaciones}
            esListaRequisicionEstadisticas={props?.esListaRequisicionEstadisticas}
            row={item}
            tit={props?.titulo}
            titulo={props?.titulo === "Herramientas"}
            enAccion={(accion) => props?.enAccion && props?.enAccion(accion, item)}
          />
        );
      }

      keys.forEach((key) => {
        row[key] = renderCell(item[key], item, key);
      });
      row["__searchText__"] = keys
        .map((key) => String(item[key] ?? ""))
        .join(" ")
        .toLowerCase();

      return row;
    });

    return { columns, rows };
  };

  const handleFiltro = useCallback(
    (textoFiltrar: string) => {
      if (props.canSearch) return;
      const text_ = textoFiltrar
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

      if (_.isEmpty(text_)) {
        setData(props?.data || []);
        return;
      }

      const rawKeys = Object.keys((props?.data || [])?.[0] || {});
      const columnsToFilter = rawKeys.filter((e) => !e.includes("id") && !e.includes("fecha"));

      setData(
        (props?.data || []).filter((c: any) => {
          return columnsToFilter.some(
            (key) =>
              typeof c?.[key] === "string" &&
              c[key]
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .includes(text_.trim())
          );
        })
      );
    },
    [props?.data, props?.canSearch]
  );

  useEffect(() => {
    setData(props?.data || []);
  }, [props?.data]);

  return {
    darkMode,
    handleFiltro,
    data,
    buildMDColumns,
  };
};
